/**
 * Customer filter operator vocabulary.
 *
 * Three lookup tables, and not one of them is a Zod schema — which is why they
 * sit beside `customer.schemas.ts` rather than inside it. This file imports
 * nothing: no `zod`, no enums, no sibling schema. The band it was lifted out of
 * was the only region of that file with no parse, no `z.object` and no
 * inference, and the three tables compose only with each other.
 *
 * ## Both ends read this, and the server end is an allowlist
 *
 * 🚨 `FILTER_OPERATORS` is NOT a UI table that the backend happens to mirror. It
 * is the single definition both ends derive from, and the backend's derivation
 * is what REFUSES a request:
 *
 * - `apps/backend/src/customers/domain/value-objects/customer-filter-spec.ts`
 *   declares `CustomerFilterFieldKind = keyof typeof FILTER_OPERATORS` and
 *   builds `CUSTOMER_FILTER_OPERATORS` by spreading this table, adding the one
 *   `customField` entry as the union of its own values.
 * - `apps/backend/src/customers/domain/utils/parse-customer-list-filters.ts`
 *   then looks a field's kind up in that map and rejects any operator absent
 *   from it.
 *
 * So editing a row here changes what `GET /api/customers` ACCEPTS, not just what
 * a dropdown offers. That file's own docblock states the contract from the other
 * side — *"DERIVED, never restated … so the two cannot disagree about which
 * operator a field offers"* — and names the defect a second spelling produced:
 * `channel` offered `empty` / `not_empty` in the dropdown while the query
 * builder returned `null` for both and the list came back unfiltered.
 *
 * The render side is `apps/frontend/src/modules/Contacts/components/FilterBuilder.tsx`,
 * its `ViewToolbar/FilterPills.tsx`, and the Workflow canvas's
 * `platform-listener/FilterBuilder.tsx`.
 *
 * ## The three are one unit
 *
 * `FIELD_TYPE_TO_OPERATOR_CATEGORY` is annotated `keyof typeof FILTER_OPERATORS`
 * and `OPERATOR_LABELS` labels the operators that table holds, so splitting them
 * across files buys nothing and costs a cross-file type edge.
 *
 * ## How it is reached
 *
 * Through `./schemas` — this path is never imported directly from outside this
 * directory. Consumers outside the package import from `@nexus/types` or
 * `@nexus/types/contracts`, which are both `api/index.ts`, and that file
 * re-exports these three by name from `./domains/customers`. The Public API v1
 * contract is the hard constraint: `contract-layering.test.ts` allows it to reach
 * `api/domains/customers/schemas` as a DIRECTORY, in a three-entry allowlist
 * whose own header says a fourth instance needs an edit there. A narrower
 * specifier would therefore demand an allowlist row that gate exists to refuse.
 *
 * The field-type keys of `FIELD_TYPE_TO_OPERATOR_CATEGORY` are the members of
 * `CUSTOMER_FIELD_TYPES`, declared in `./customer.schemas` — named by file here
 * rather than by position, because the two no longer share one.
 */

/**
 * Filter operators by category.
 *
 * Operators are grouped by the data type they apply to:
 *
 * | Category    | Operators                                          | Field types                              |
 * |-------------|----------------------------------------------------|------------------------------------------|
 * | string      | eq, neq, contains, not_contains, empty, not_empty  | text, longtext, email, phone, url        |
 * | numeric     | eq, neq, gt, gte, lt, lte, empty, not_empty        | number, currency, percent, rating        |
 * | date        | eq, before, after, on_or_before, on_or_after, empty, not_empty | date                          |
 * | enum        | eq, neq, empty, not_empty                          | select, channel                          |
 * | multi       | has_any, has_all, has_none, empty, not_empty        | multiselect, tags                        |
 * | boolean     | is_true, is_false                                  | checkbox                                 |
 */
export const FILTER_OPERATORS = {
  string: ["eq", "neq", "contains", "not_contains", "empty", "not_empty"],
  numeric: ["eq", "neq", "gt", "gte", "lt", "lte", "empty", "not_empty"],
  date: ["eq", "before", "after", "on_or_before", "on_or_after", "empty", "not_empty"],
  enum: ["eq", "neq", "empty", "not_empty"],
  multi: ["has_any", "has_all", "has_none", "empty", "not_empty"],
  boolean: ["is_true", "is_false"]
} as const;

export const OPERATOR_LABELS: Record<string, string> = {
  eq: "is",
  neq: "is not",
  contains: "contains",
  not_contains: "does not contain",
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
  before: "is before",
  after: "is after",
  on_or_before: "is on or before",
  on_or_after: "is on or after",
  has_any: "has any of",
  has_all: "has all of",
  has_none: "has none of",
  empty: "is empty",
  not_empty: "is not empty",
  is_true: "is checked",
  is_false: "is not checked"
};

/** Maps custom field types to their operator category */
export const FIELD_TYPE_TO_OPERATOR_CATEGORY: Record<string, keyof typeof FILTER_OPERATORS> = {
  text: "string",
  longtext: "string",
  email: "string",
  phone: "string",
  url: "string",
  number: "numeric",
  currency: "numeric",
  percent: "numeric",
  rating: "numeric",
  date: "date",
  select: "enum",
  multiselect: "multi",
  checkbox: "boolean"
};
